import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { Box, Group, Divider, UnstyledButton } from '@mantine/core';
import Button from '../../../new_arch/components/Button/Button';
import Text from '../../../new_arch/modules/ui/components/Text/Text';
import Callout from '../../../new_arch/modules/ui/components/Callout/Callout';
import PlanSettingsPresenter from '../../../presenters/PlanSettings/PlanSettingsPresenter';

import './ActiveSubscriptionDetail.scss';

const TOTAL_AMOUNT_STYLE = { fontFamily: 'Open Sans', fontSize: '28px', fontWeight: 700, lineHeight: '28px', letterSpacing: '-0.53px', color: 'var(--color-text-primary)' };
const PER_MONTH_STYLE = { fontFamily: 'Open Sans', fontSize: '12px', fontWeight: 700, lineHeight: '28px', letterSpacing: '-0.53px', color: 'var(--color-text-primary)' };
const BILLED_NOTE_STYLE = { fontFamily: 'Open Sans', fontSize: '10px', fontWeight: 400, lineHeight: '14px', letterSpacing: '0.11px', color: 'var(--mantine-color-dark-3)' };
const RENEWS_TEXT_STYLE = { fontFamily: 'Open Sans', fontSize: '14px', fontWeight: 600, lineHeight: '22px', letterSpacing: '0.14px', color: 'var(--mantine-color-dark-3)' };
const GO_TO_BILLING_STYLE = { fontFamily: 'Open Sans', fontSize: '14px', fontWeight: 600, lineHeight: '22px', letterSpacing: '0px', color: 'var(--color-primary-base)' };
const PLAN_ROW_LABEL_STYLE = { fontFamily: 'Open Sans', fontSize: '14px', fontWeight: 400, lineHeight: '22px', letterSpacing: '0px', color: 'var(--mantine-color-dark-3)' };
const PLAN_ROW_PRICE_STYLE = { fontFamily: 'Open Sans', fontSize: '14px', fontWeight: 400, lineHeight: '22px', letterSpacing: '0px', color: 'var(--color-text-primary)' };

const FreeTrialHeader = observer( ( { presenter } ) => (
	<Group position="apart" align="center">
		<Group spacing={4} align="baseline">
			<Text style={{ fontSize: '28px', fontWeight: 700, lineHeight: '28px', letterSpacing: '-0.53px', color: 'var(--color-text-primary)', marginRight: '4px' }}>{presenter.daysLeftOnFreeTrial}</Text>
			<Text variant="body" style={{ color: 'var(--color-text-primary)' }}>days left until free trial ends</Text>
		</Group>
		<Button
			style={{ minWidth: '140px' }}
			disabled={presenter.upgradeButtonData.disabled}
			size="md"
			type="primary"
			onClick={presenter.onUpgradePlanButtonClicked}
		>
			{presenter.upgradeButtonData.text}
		</Button>
	</Group>
) );

FreeTrialHeader.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

const PlanRow = ( { label, price } ) => (
	<tr>
		<td style={{ padding: '6px 0' }}><Text style={PLAN_ROW_LABEL_STYLE}>{label}</Text></td>
		<td style={{ padding: '6px 0', textAlign: 'right', verticalAlign: 'middle' }}><Text style={PLAN_ROW_PRICE_STYLE}>{price}</Text></td>
	</tr>
);

PlanRow.propTypes = {
	label: PropTypes.string.isRequired,
	price: PropTypes.string.isRequired
};

export const PlanDetails = observer( ( { presenter, showPerMonth = true } ) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px 24px', borderRadius: '8px', backgroundColor: 'var(--color-bg-container)' }}>
		<Text variant="h2" style={{ color: 'var(--color-text-primary)' }}>{presenter.planName}</Text>
		<table style={{ width: '100%', borderCollapse: 'collapse' }}>
			<tbody>
				<PlanRow label={presenter.planRowLabel} price={presenter.planCostForDisplay} />
				<PlanRow label={presenter.membersAddOnRowLabel} price={presenter.membersAddOnCostForDisplay} />
				<PlanRow label={presenter.storageAddOnRowLabel} price={presenter.storageAddOnCostForDisplay} />
			</tbody>
		</table>
		<Divider sx={{ borderColor: 'var(--color-outline-strong)' }} />
		<Group position="apart" align="flex-start">
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
				<Text variant="h2" style={{ color: 'var(--color-text-primary)' }}>Total</Text>
				{presenter.billingNote && (
					<Text style={BILLED_NOTE_STYLE}>
						*{presenter.billingNote}
					</Text>
				)}
			</Box>
			<Box sx={{ textAlign: 'right' }}>
				<Text style={TOTAL_AMOUNT_STYLE}>{presenter.totalCostForDisplay}</Text>
				{showPerMonth && <Text style={PER_MONTH_STYLE}>per month*</Text>}
			</Box>
		</Group>
	</Box>
) );

PlanDetails.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired,
	showPerMonth: PropTypes.bool
};

const SubscriptionFooter = observer( ( { presenter } ) => (
	<Group position="apart" align="flex-start">
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
			<Group spacing={4}>
				<Text style={RENEWS_TEXT_STYLE}>Renews:</Text>
				<Text style={RENEWS_TEXT_STYLE}>{presenter.formatedBillingPeriodEnd}</Text>
			</Group>
			<UnstyledButton onClick={presenter.onGoToBillingClicked}>
				<Text style={GO_TO_BILLING_STYLE}>Go to Billing</Text>
			</UnstyledButton>
		</Box>
		<Button
			className="ActiveSubscriptionDetail__change-plan-btn"
			style={{ minWidth: '140px' }}
			disabled={presenter.upgradeButtonData.disabled}
			size="md"
			type="secondary"
			onClick={presenter.onUpgradePlanButtonClicked}
		>
			{presenter.upgradeButtonData.text}
		</Button>
	</Group>
) );

SubscriptionFooter.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

const ActiveSubscriptionDetail = ( { presenter } ) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '24px',
			padding: '24px',
			borderRadius: '24px',
			border: '2px solid var(--mantine-color-green-6)',
			backgroundColor: 'var(--mantine-color-green-0)',
			width: '100%',
			maxWidth: '744px',
			boxSizing: 'border-box'
		}}
	>
		{presenter.isOnActiveFreeTrial
			? <FreeTrialHeader presenter={presenter} />
			: null}
		<PlanDetails presenter={presenter} />
		{presenter.isOnActiveFreeTrial && presenter.trialSeatsAdded > 0 && (
			<Callout
				type="promotional"
				variant="outlined"
				text={`You added ${presenter.trialSeatsAdded} ${presenter.trialSeatsAdded === 1 ? 'seat' : 'seats'} during your trial. You can adjust seats below.`}
				styles={{ text: { lineHeight: '22px' } }}
			/>
		)}
		{presenter.workspace.subscription?.inPlanTrial && (
			<Group position="right">
				<Button
					className="ActiveSubscriptionDetail__change-plan-btn"
					style={{ minWidth: '140px' }}
					disabled={presenter.upgradeButtonData.disabled}
					size="md"
					type="secondary"
					onClick={presenter.onChangePlanButtonClicked}
				>
					Change Plan
				</Button>
			</Group>
		)}
		{!presenter.isOnActiveFreeTrial && <SubscriptionFooter presenter={presenter} />}
	</Box>
);

ActiveSubscriptionDetail.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

export default observer( ActiveSubscriptionDetail );
